import NewRelicUtils from 'config/monitoring/NewRelicUtils'
import useBoundState from 'store'

import InterWebviewBridge, {
  interWbNavigate,
  interWbSession,
  IWbTac,
  IWbTic,
  WbSessionActions,
  interWbAuth,
  IWbISafeResponse,
} from '@interco/inter-webview-bridge'

import { BridgeMockAppInfo, BridgeMockUserInfo } from './BridgeMock'

interface IRequestISafe {
  category: string
  checkType: string
  feature: string
  value: number
}

export class BaseBridge {
  private static backNavigationHandler: (() => void) | null = null

  private static handleError(error: unknown, errorCodeRef: string): void {
    NewRelicUtils.noticeError(error as Error, { errorCodeRef })
  }

  private static addUrlProtocol(url: string) {
    const HTTPS_PROTOCOL = 'https://'
    const HTTP_PROTOCOL = 'http://'
    if (url.startsWith(HTTPS_PROTOCOL) || url.startsWith(HTTP_PROTOCOL)) {
      return url
    }
    return HTTPS_PROTOCOL + url
  }

  static isBrowser() {
    const iWb = InterWebviewBridge.getInstance()

    if (process.env.REACT_APP_BRIDGE_DEBUG === 'true') {
      iWb.setDebug(true)
    }

    return iWb.getEnvironment() === 'BROWSER'
  }

  static isIOS() {
    const iWb = InterWebviewBridge.getInstance()

    if (process.env.REACT_APP_BRIDGE_DEBUG === 'true') {
      iWb.setDebug(true)
    }

    return iWb.getEnvironment() === 'IOS'
  }

  static isAndroid() {
    const iWb = InterWebviewBridge.getInstance()

    if (process.env.REACT_APP_BRIDGE_DEBUG === 'true') {
      iWb.setDebug(true)
    }

    return iWb.getEnvironment() === 'ANDROID'
  }

  static async openDeepLink(deepLink: string) {
    if (this.isBrowser()) {
      window.open(BaseBridge.addUrlProtocol(deepLink), '_blank')?.focus()
      return
    }
    try {
      await interWbNavigate.openDeepLink(deepLink)
    } catch (e) {
      NewRelicUtils.noticeError(e as Error, {
        errorCodeRef: 'BridgeService.openDeepLink',
      })
    }
  }

  static async openDeepLinkIOs(deepLink: string) {
    const iWb = InterWebviewBridge.getInstance()

    if (!BaseBridge.isBrowser()) {
      try {
        await iWb.execute({ action: 'openDeeplinkBS', metadata: { url: deepLink } })
      } catch (error) {
        NewRelicUtils.noticeError(error as Error, {
          errorCodeRef: 'BaseBridge.openDeepLinkIOs',
        })
      }
    }
  }

  static callPhoneNumber(phoneNumber: string) {
    if (!BaseBridge.isBrowser()) {
      interWbNavigate.callPhoneNumber(phoneNumber)
    }
  }

  static addBackListener(event: () => void) {
    if (!BaseBridge.isBrowser()) {
      const iWb = InterWebviewBridge.getInstance()

      if (BaseBridge.backNavigationHandler) {
        iWb.removeWebviewEventListener(WbSessionActions.GO_BACK, BaseBridge.backNavigationHandler)
      }

      iWb.addWebviewEventListener(WbSessionActions.GO_BACK, event)

      BaseBridge.backNavigationHandler = event
    }
  }

  static requestGoBack() {
    if (!BaseBridge.isBrowser()) {
      interWbNavigate.requestGoBack()
    }
  }

  static async requestBabi() {
    if (!BaseBridge.isBrowser()) {
      try {
        await interWbNavigate.requestBabi()
      } catch (error) {
        NewRelicUtils.noticeError(error as Error, {
          errorCodeRef: 'BaseBridge.requestBabi',
        })
      }
    }
  }

  static async getUserInfo() {
    if (BaseBridge.isBrowser()) {
      return BridgeMockUserInfo
    }

    try {
      return await interWbSession.getUserInfo()
    } catch (error) {
      NewRelicUtils.noticeError(error as Error, {
        errorCodeRef: 'BaseBridge.getUserInfo',
      })

      return Promise.reject(error)
    }
  }

  static async getAppInfo() {
    if (BaseBridge.isBrowser()) {
      return BridgeMockAppInfo
    }

    try {
      return await interWbSession.getAppInfo()
    } catch (error) {
      NewRelicUtils.noticeError(error as Error, {
        errorCodeRef: 'BaseBridge.getAppInfo',
      })

      return Promise.reject(error)
    }
  }

  static async openPdf(url: string, title: string): Promise<void> {
    if (this.isBrowser()) {
      window.open(BaseBridge.addUrlProtocol(url), '_blank')?.focus()
      return
    }
    try {
      await interWbNavigate.openPdf(title, url)
    } catch (e) {
      NewRelicUtils.noticeError(e as Error, {
        errorCodeRef: 'BridgeService.openPdf.displayPdf',
      })
    }
  }

  static requestAnalytics(name: string, params?: Record<string, string>) {
    const { utmCampaign } = useBoundState.getState()

    const addUtmAnalytics = {
      ...params,
      utm_campaign: utmCampaign,
    }

    if (!BaseBridge.isBrowser()) {
      interWbSession.requestAnalytics(name, addUtmAnalytics)
    } else {
      // eslint-disable-next-line no-console
      // console.log('nome do evento: ', name, 'Params: ', params ?? '')
    }
  }

  static hideNativeToolbar() {
    if (!BaseBridge.isBrowser()) {
      interWbSession.editToolbar('name', false)
    }
  }

  static async ticWb(ticProps: IWbTic): Promise<void> {
    if (!this.isBrowser()) {
      try {
        await interWbSession.tic(ticProps)
      } catch (error) {
        this.handleError(error, 'BaseBridge.ticWb')
      }
    }
  }

  static async tacWb(tacProps: IWbTac): Promise<void> {
    if (!this.isBrowser()) {
      try {
        await interWbSession.tac(tacProps)
      } catch (error) {
        this.handleError(error, 'BaseBridge.tacWb')
      }
    }
  }

  static async requestISafe(data: IRequestISafe): Promise<IWbISafeResponse> {
    try {
      if (!this.isBrowser()) {
        return await interWbAuth.requestISafe(
          data.category,
          data.checkType,
          data.feature,
          data.value,
        )
      }
      return Promise.resolve({
        authenticationToken: '',
        authenticationType: '',
        authenticationValue: '',
        code: '',
        transactionType: '',
      })
    } catch (error) {
      NewRelicUtils.noticeError(error as Error, {
        errorCodeRef: 'BaseBridge.requestISafe',
      })

      return Promise.reject(error)
    }
  }
}

export default BaseBridge
